import { Check } from "lucide-react";

/**
 * Iter-166 — Unified 3-step funnel progress indicator.
 *
 * Shared between the homepage QuoteForm + every intake modal + every
 * post-submit success card so the user sees the same journey markers
 * across every entry surface.
 *
 * The three steps are intentionally generic + truthful:
 *   1. Enter Details          (the user is filling out the form)
 *   2. Matching Providers     (the brief 1-2 sec transition state)
 *   3. Compare Options        (the partner-routing / comparison surface)
 *
 * State machine:
 *   - step=1, state="active"    → user is on the form
 *   - step=2, state="matching"  → submission in flight + matching anim
 *   - step=3, state="ready"     → success card visible, CTA armed
 *
 * Iter-182 — Stripe / Mercury / Ramp-style polish:
 *   - Equal-width column grid keeps bubbles perfectly aligned.
 *   - Connector hairlines between bubbles (emerald=complete · slate=pending).
 *   - Stronger active state: emerald ring + white-on-emerald number.
 *   - Stronger complete state: emerald solid + check.
 *   - Inactive: slate-300 hairline ring.
 *   - Bubble centered above label so each column is a complete unit.
 *
 * Visual contract:
 *   - Active step       → emerald ring + emerald background, white number
 *   - Complete step     → emerald solid background + emerald connector
 *   - Inactive step     → white background, slate-300 ring, slate number
 */
export const FUNNEL_STEPS = [
  { id: 1, label: "Enter Details", short_label: "Details" },
  { id: 2, label: "Match Providers", short_label: "Match" },
  { id: 3, label: "See Options", short_label: "Compare" },
];

export default function FunnelProgress({
  step = 1,
  state = "active",
  compact = false,
  className = "",
}) {
  // Derive each step's visual state from the active step + funnel state.
  const getStepState = (id) => {
    if (id < step) return "complete";
    if (id === step) {
      if (id === 3 && state === "ready") return "ready";
      if (id === 2 && state === "matching") return "matching";
      return "active";
    }
    if (step === 3 && state === "ready" && id < 3) return "complete";
    return "inactive";
  };

  return (
    <ol
      data-testid="funnel-progress"
      data-funnel-step={step}
      data-funnel-state={state}
      aria-label="Three-step funnel progress"
      className={`grid grid-cols-3 gap-0 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.12em] ${
        compact ? "" : "mb-5"
      } ${className}`}
    >
      {FUNNEL_STEPS.map((s, idx) => {
        const stepState = getStepState(s.id);
        const isActive = stepState === "active";
        const isMatching = stepState === "matching";
        const isComplete = stepState === "complete" || stepState === "ready";
        const isInactive = stepState === "inactive";

        // Connector hairlines flank each bubble. The line BEFORE this
        // bubble lights up emerald once the previous step is complete;
        // the line AFTER lights up once THIS bubble is complete.
        const prevComplete = idx > 0 && getStepState(s.id - 1) !== "inactive" && (
          getStepState(s.id - 1) === "complete" ||
          getStepState(s.id - 1) === "ready"
        );
        const nextLineActive = isComplete;

        return (
          <li
            key={s.id}
            data-testid={`funnel-step-${s.id}`}
            data-step-state={stepState}
            className="flex flex-col items-center gap-1.5 sm:gap-2 relative px-1"
          >
            {/* Connector line going left (hidden on first step) */}
            {idx > 0 && (
              <span
                aria-hidden="true"
                className={`absolute top-3 sm:top-3.5 left-0 right-1/2 h-[2px] -ml-1 ${
                  prevComplete ? "bg-[#10B981]" : "bg-slate-200"
                } transition-colors duration-300`}
              />
            )}
            {/* Connector line going right (hidden on last step) */}
            {idx < FUNNEL_STEPS.length - 1 && (
              <span
                aria-hidden="true"
                className={`absolute top-3 sm:top-3.5 left-1/2 right-0 h-[2px] -mr-1 ${
                  nextLineActive ? "bg-[#10B981]" : "bg-slate-200"
                } transition-colors duration-300`}
              />
            )}

            {/* Bubble */}
            <span
              aria-hidden="true"
              className={`relative z-10 w-6 h-6 sm:w-7 sm:h-7 grid place-items-center rounded-full font-display shrink-0 transition-all duration-300 ${
                isComplete
                  ? "bg-[#10B981] text-white"
                  : isActive
                  ? "bg-white text-[#0A2540] ring-2 ring-[#10B981] shadow-focusRing"
                  : isMatching
                  ? "bg-[#10B981] text-white animate-pulse"
                  : "bg-white text-slate-500 ring-1 ring-slate-300"
              }`}
            >
              {isComplete ? (
                <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={3} />
              ) : (
                <span className="text-[10px] sm:text-[11px] tabular-nums">{s.id}</span>
              )}
            </span>

            {/* Label — centered under the bubble. Slightly de-emphasized
                for inactive steps to draw the eye to the active one.
                Iter-182.1 — Mobile uses single-word `short_label` to
                prevent the iter-182 first-draft overlap on 390px screens
                (e.g., "MATCHING PROVIDERS" was clipping into adjacent
                columns). Desktop renders the full label. `whitespace-
                nowrap` is removed; we rely on the short_label fitting
                in one line per column. */}
            <span
              className={`text-center leading-tight ${
                isActive
                  ? "text-slate-950"
                  : isComplete
                  ? "text-[#0A2540]"
                  : "text-slate-500"
              }`}
            >
              <span className="sm:hidden">{s.short_label}</span>
              <span className="hidden sm:inline">{s.label}</span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}
